import React, { useRef, useEffect, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

interface SignaturePadProps {
    value: string | null;             // Base64 data URL
    onChange: (dataUrl: string | null) => void;
    clearLabel: string;
    hint: string;
    ariaLabel: string;
}

// Tracks parent width so the canvas re-fits on rotation/resize. The lib
// repaints on mount only — without manual sizing it gets stretched.
function useElementWidth<T extends HTMLElement>() {
    const ref = useRef<T | null>(null);
    const [width, setWidth] = useState(0);
    useEffect(() => {
        if (!ref.current) return;
        const el = ref.current;
        const update = () => setWidth(el.clientWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return { ref, width };
}

const SignaturePad: React.FC<SignaturePadProps> = ({ value, onChange, clearLabel, hint, ariaLabel }) => {
    const sigRef = useRef<SignatureCanvas | null>(null);
    const { ref: wrapRef, width } = useElementWidth<HTMLDivElement>();
    const [isEmpty, setIsEmpty] = useState<boolean>(!value);
    // True when `value` last changed because we just pushed it up — in that case
    // the canvas already holds the live strokes, so re-painting via fromDataURL
    // would draw a trimmed copy on top of the originals (visible duplication).
    const skipNextRestoreRef = useRef(false);

    // Restore an externally-provided signature (e.g. when the user navigates
    // back to step 6 after drawing earlier). Guarded so it doesn't fire on the
    // value our own onEnd handler just emitted.
    useEffect(() => {
        if (skipNextRestoreRef.current) {
            skipNextRestoreRef.current = false;
            return;
        }
        if (value && sigRef.current && width > 0) {
            sigRef.current.fromDataURL(value);
            setIsEmpty(false);
        }
    }, [value, width]);

    const handleEnd = () => {
        if (!sigRef.current) return;
        if (sigRef.current.isEmpty()) {
            setIsEmpty(true);
            skipNextRestoreRef.current = true;
            onChange(null);
        } else {
            // Trim before exporting so the stored PNG is just the strokes, not
            // a full transparent canvas — also gives a stable PDF render.
            const trimmed = sigRef.current.getTrimmedCanvas().toDataURL('image/png');
            setIsEmpty(false);
            skipNextRestoreRef.current = true;
            onChange(trimmed);
        }
    };

    const handleClear = () => {
        sigRef.current?.clear();
        setIsEmpty(true);
        skipNextRestoreRef.current = true;
        onChange(null);
    };

    const height = 180;

    return (
        <div className="w-full">
            <div
                ref={wrapRef}
                className="relative w-full rounded-2xl border-2 border-dashed border-[#384860]/30 bg-white"
                style={{ height }}
            >
                {width > 0 && (
                    <SignatureCanvas
                        ref={sigRef}
                        penColor="#384860"
                        backgroundColor="rgba(255,255,255,0)"
                        canvasProps={{
                            width,
                            height,
                            className: 'rounded-2xl touch-none',
                            'aria-label': ariaLabel,
                        }}
                        onEnd={handleEnd}
                    />
                )}
                {isEmpty && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <span className="text-sm text-[#384860]/50 select-none">{hint}</span>
                    </div>
                )}
            </div>

            <div className="mt-2 flex justify-end">
                <button
                    type="button"
                    onClick={handleClear}
                    className="text-sm font-medium text-[#384860] hover:text-[#0E7490] transition-colors cursor-pointer"
                    aria-label={clearLabel}
                >
                    {clearLabel}
                </button>
            </div>
        </div>
    );
};

export default SignaturePad;
